/**
 * Study Paths API calls with React Query. Every key is prefixed with
 * `QUERY_KEYS.studyPaths.root`. Callers should call `invalidateStudyPathQueries` after a
 * mutation, which refetches this screen's own (already-mounted) queries and marks any other
 * already-mounted Study Paths screen's queries (e.g. navigating from the phase form back to the
 * list) stale so they don't serve pre-mutation cache without needing their own refresh signal
 * baked into the query key - that previously minted a fresh cache entry per mutation, leaving
 * every earlier one behind to also get swept up (and eagerly refetched) by the next invalidation.
 */
import {
    GetProgrammeStudyPathList,
    getProgrammeStudyPathDetails,
    getProgrammeStudyPathModuleList,
    getStudyPathUnassignedModules,
} from '../../services/ProgramService';
import { appQueryClient } from '../appQueryClient';
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from '../queryConsts';
import { useBaseReactQuery } from './useBaseReactQuery';

const EMPTY_LIST = Object.freeze({ data: [], total: 0 });

/**
 * Invalidates this programme version's Study Paths queries (optionally scoped further to one
 * phase's own details).
 * The phase form and the list/re-arrange screens are separate mounts, so a save on one
 * doesn't refetch the other's already-cached data - the shared hook defaults to
 * refetchOnMount false, which would otherwise show pre-mutation data on navigating back.
 * Scoped by `programmeId`/`versionId` (and `phaseId` for `phaseDetails`) rather than the whole
 * `studyPaths.root` prefix, so it doesn't also refetch every other phase/programme/version
 * still sitting in the cache from earlier in the session.
 * NOTE for refetchMountedModuleLists: "active" refetches only module-list queries an AssignModulesModal/PhaseForm
 * group table is currently mounted and observing (i.e. the page/filters someone's actually looking at right now),
 * and just marks any other cached page/search/sort combination for this programme version stale instead of eagerly
 * refetching it too.
 * @param {string | number} programmeId PMC programme id.
 * @param {string | number} versionId Programme version id.
 * @param {string | number} [phaseId] The phase being saved/edited, to also refresh its `phaseDetails` cache.
 */
export function invalidateStudyPathQueries(programmeId, versionId, phaseId) {
    const matchesProgrammeVersion = (requestData) =>
        String(requestData?.programme_id) === String(programmeId) && String(requestData?.version_id) === String(versionId);

    const eagerRefetch = appQueryClient.invalidateQueries({
        predicate: (query) => {
            const [root, type, ...rest] = query.queryKey;
            if (root !== QUERY_KEYS.studyPaths.root || type === QUERY_KEYS.studyPaths.assignModulesList) {
                return false;
            }
            if (type === QUERY_KEYS.studyPaths.phaseDetails) {
                const [queryPhaseId] = rest;
                return phaseId != null && String(queryPhaseId) === String(phaseId);
            }
            if (type === QUERY_KEYS.studyPaths.unassignedModules) {
                const [queryProgrammeId, queryVersionId] = rest;
                return String(queryProgrammeId) === String(programmeId) && String(queryVersionId) === String(versionId);
            }
            const [requestData] = rest;
            return matchesProgrammeVersion(requestData);
        },
        refetchType: "all"
    });

    const refetchMountedModuleLists = appQueryClient.invalidateQueries({
        predicate: (query) => {
            const [root, type, ...rest] = query.queryKey;
            if (root !== QUERY_KEYS.studyPaths.root || type !== QUERY_KEYS.studyPaths.assignModulesList) {
                return false;
            }
            const [requestData] = rest;
            return matchesProgrammeVersion(requestData);
        },
        refetchType: "active"
    });

    return Promise.all([eagerRefetch, refetchMountedModuleLists]);
}

/**
 * Study Paths - a programme version's phase list (SPEC1/SPEC2), including each phase's
 * eager-loaded modules and live-computed supplied credits.
 * @param {object} requestData `{programme_id, version_id, page, limit, key, sort, search}`.
 * @param {boolean} [enabled=true] Skip the request until the programme/version has loaded.
 */
export function useStudyPathPhaseList(requestData, enabled = true) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.studyPaths.root, QUERY_KEYS.studyPaths.phaseList, requestData],
        async () => {
            const res = await GetProgrammeStudyPathList(requestData);
            return {
                data: res.data?.data?.data ?? [],
                total: res.data?.data?.total ?? 0,
            };
        },
        { cacheProfile: QUERY_CACHE_PROFILES.TABLE, enabled }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_LIST,
    };
}

/**
 * Study Paths - a single phase's stored details (Phase Name/Year/Semester-Term/credit targets).
 * @param {string | number} phaseId Phase id, or falsy to skip (create mode).
 */
export function useStudyPathPhaseDetails(phaseId) {
    return useBaseReactQuery(
        [QUERY_KEYS.studyPaths.root, QUERY_KEYS.studyPaths.phaseDetails, phaseId],
        async () => {
            const res = await getProgrammeStudyPathDetails(phaseId);
            return res.data?.data ?? null;
        },
        { cacheProfile: QUERY_CACHE_PROFILES.DEFAULT, enabled: Boolean(phaseId) }
    );
}

/**
 * Study Paths - modules linked to the programme version but not placed in any phase (SPEC1 R4 /
 * SPEC3 R3 Unassigned Modules panel).
 * @param {string | number} programmeId PMC programme id.
 * @param {string | number} versionId Programme version id.
 */
export function useStudyPathUnassignedModules(programmeId, versionId) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.studyPaths.root, QUERY_KEYS.studyPaths.unassignedModules, programmeId, versionId],
        async () => {
            const res = await getStudyPathUnassignedModules(programmeId, versionId);
            return res.data?.modules ?? [];
        },
        { cacheProfile: QUERY_CACHE_PROFILES.TABLE, enabled: Boolean(programmeId) }
    );

    return {
        ...query,
        data: query.data ?? [],
    };
}

/**
 * Study Paths - a module group's candidate list, used both by the simple "assigned modules"
 * table (SPEC3 R2) and the Assign Modules modal (SPEC4), which needs the same data plus its own
 * extra display columns already attached server-side.
 * @param {object} requestData `{id, isAssigned, module_type, programme_id, version_id, search,
 *   key, sort, page, limit, term, year}`.
 * @param {boolean} [enabled=true] Skip the request until the phase/group is known.
 */
export function useStudyPathModuleList(requestData, enabled = true) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.studyPaths.root, QUERY_KEYS.studyPaths.assignModulesList, requestData],
        async () => {
            const res = await getProgrammeStudyPathModuleList(requestData);
            return {
                data: res.data?.modules?.data ?? [],
                total: res.data?.modules?.total ?? 0,
            };
        },
        { cacheProfile: QUERY_CACHE_PROFILES.TABLE, enabled }
    );

    return {
        ...query,
        data: query.data ?? EMPTY_LIST,
    };
}
